import React, { useEffect, useMemo, useState } from 'react';
import * as workersApi from '../api/workers.js';
import { useT } from '../i18n.jsx';
import { exactMoney, BindingList } from './WorkersWorkspace.jsx';
import { TrafficBars } from './WorkersCharts.jsx';
import {
  KpiCard,
  KpiGrid,
  StatusCodePill,
  MethodPill,
} from '../pages/workspace-sections/analytics-shared.jsx';

const percent = (value) =>
  Number.isFinite(value) ? `${value.toFixed(value >= 99 ? 2 : 1)}%` : '—';

const duration = (milliseconds) => {
  if (!Number.isFinite(milliseconds)) return '—';
  if (milliseconds < 1000) return `${Math.round(milliseconds)} ms`;
  if (milliseconds >= 60 * 60 * 1000) {
    return `${(milliseconds / (60 * 60 * 1000)).toFixed(1)} h`;
  }
  if (milliseconds >= 60 * 1000) {
    return `${(milliseconds / (60 * 1000)).toFixed(1)} min`;
  }
  return `${(milliseconds / 1000).toFixed(2)} s`;
};

const age = (value, now = Date.now()) => {
  const started = new Date(value).getTime();
  if (!Number.isFinite(started)) return '—';
  const seconds = Math.max(0, Math.floor((now - started) / 1000));
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (days) return `${days}d ${hours}h`;
  if (hours) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

const quantile = (values, point) => {
  const sorted = values
    .map(Number)
    .filter(Number.isFinite)
    .sort((left, right) => left - right);
  if (!sorted.length) return null;
  return sorted[
    Math.min(sorted.length - 1, Math.ceil(sorted.length * point) - 1)
  ];
};

const weight = (item) => Math.max(1, Number(item.sampleInterval) || 1);

const validTimestamp = (value) => {
  const normalized =
    typeof value === 'string' &&
    /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}(?:\.\d+)?$/.test(value)
      ? `${value.replace(' ', 'T')}Z`
      : value;
  const time = new Date(normalized).getTime();
  return Number.isFinite(time) ? time : null;
};

export function summarizeWorkerTelemetry(
  items = [],
  logs = [],
  bucketCount = 12,
) {
  const calls = items.filter((item) => validTimestamp(item.timestamp) !== null);
  const total = calls.reduce((sum, item) => sum + weight(item), 0);
  const requestSuccess = calls.reduce(
    (sum, item) => sum + (Number(item.status) < 400 ? weight(item) : 0),
    0,
  );
  const serverFailures = calls.reduce(
    (sum, item) => sum + (Number(item.status) >= 500 ? weight(item) : 0),
    0,
  );
  const exceptions = logs.filter(
    (item) =>
      String(item.kind || '').toUpperCase() === 'EXCEPTION' ||
      String(item.level || '').toLowerCase() === 'error' ||
      String(item.outcome || '').toLowerCase() === 'exception',
  ).length;
  const times = calls.map((item) => validTimestamp(item.timestamp));
  const latestAt = times.length ? Math.max(...times) : null;
  const earliestAt = times.length ? Math.min(...times) : null;
  const naturalSpan = latestAt && earliestAt ? latestAt - earliestAt : 0;
  const windowEnd = latestAt || Date.now();
  const windowStart =
    naturalSpan > 60_000 ? earliestAt : windowEnd - 60 * 60 * 1000;
  const bucketWidth = Math.max(1, (windowEnd - windowStart) / bucketCount);
  const buckets = Array.from({ length: bucketCount }, (_, index) => ({
    start: windowStart + bucketWidth * index,
    end: windowStart + bucketWidth * (index + 1),
    calls: 0,
    failures: 0,
  }));
  calls.forEach((item) => {
    const timestamp = validTimestamp(item.timestamp);
    const index = Math.max(
      0,
      Math.min(
        bucketCount - 1,
        Math.floor((timestamp - windowStart) / bucketWidth),
      ),
    );
    buckets[index].calls += weight(item);
    if (Number(item.status) >= 500) buckets[index].failures += weight(item);
  });

  const endpointMap = new Map();
  calls.forEach((item) => {
    const key = `${item.method || '—'} ${item.path || '/'}`;
    const current = endpointMap.get(key) || {
      key,
      method: item.method || '—',
      path: item.path || '/',
      calls: 0,
      clientErrors: 0,
      serverErrors: 0,
      durations: [],
    };
    current.calls += weight(item);
    if (Number(item.status) >= 400 && Number(item.status) < 500) {
      current.clientErrors += weight(item);
    }
    if (Number(item.status) >= 500) current.serverErrors += weight(item);
    current.durations.push(Number(item.durationMs));
    endpointMap.set(key, current);
  });

  return {
    total,
    captured: calls.length,
    requestSuccessRate: total ? (requestSuccess / total) * 100 : null,
    availability: total ? ((total - serverFailures) / total) * 100 : null,
    serverFailures,
    exceptions,
    p50: quantile(
      calls.map((item) => item.durationMs),
      0.5,
    ),
    p95: quantile(
      calls.map((item) => item.durationMs),
      0.95,
    ),
    earliestAt,
    latestAt,
    buckets,
    endpoints: [...endpointMap.values()]
      .map((item) => ({ ...item, p95: quantile(item.durations, 0.95) }))
      .sort((left, right) => right.calls - left.calls)
      .slice(0, 8),
  };
}

export const WorkersMonitoringPanel = ({
  worker,
  initialEnvironmentValue,
  onEnvironmentChange,
  onOpenResource,
}) => {
  const { t } = useT();
  const initialEnvironment = worker.environments?.some(
    (item) => item.name === 'PREVIEW' && item.activeDeploymentId,
  )
    ? 'preview'
    : 'production';
  const [environment, setLocalEnvironment] = useState(
    initialEnvironmentValue || initialEnvironment,
  );
  const setEnvironment = (value) => {
    setLocalEnvironment(value);
    onEnvironmentChange?.(value);
  };
  const [data, setData] = useState({ items: [], sampled: false });
  const [logs, setLogs] = useState({ items: [], sampled: false });
  const [spend, setSpend] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshedAt, setRefreshedAt] = useState(null);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [nextData, nextLogs, nextSpend] = await Promise.all([
        workersApi.listInvocations(worker.id, environment),
        workersApi.listRuntimeLogs(worker.id, environment),
        workersApi
          .getBillingOverview(worker.id, environment)
          .catch(() => null),
      ]);
      setData(nextData);
      setLogs(nextLogs);
      setSpend(nextSpend?.data || null);
      setRefreshedAt(new Date());
    } catch (nextError) {
      setError(nextError.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [worker.id, environment]);

  const telemetry = useMemo(
    () => summarizeWorkerTelemetry(data.items, logs.items),
    [data.items, logs.items],
  );
  const selectedEnvironment = worker.environments?.find(
    (item) => item.name.toLowerCase() === environment,
  );
  const deployment = worker.deployments?.find(
    (item) => item.id === selectedEnvironment?.activeDeploymentId,
  );
  const latestDelay = telemetry.latestAt
    ? Math.max(0, Date.now() - telemetry.latestAt)
    : null;
  const unhealthy = telemetry.serverFailures > 0 || telemetry.exceptions > 0;

  return (
    <section className="card workers-monitoring" aria-busy={loading}>
      <header className="workers-monitor-head">
        <div>
          <strong>{t('wk.tab.health')}</strong>
        </div>
        <div className="workers-monitor-controls">
          <label>
            <span className="micro">{t('wk.env')}</span>
            <select
              className="input mono"
              value={environment}
              onChange={(event) => setEnvironment(event.target.value)}
            >
              <option value="preview">PREVIEW</option>
              <option value="production">PRODUCTION</option>
            </select>
          </label>
          <button className="btn btn-sm" disabled={loading} onClick={load}>
            {loading ? t('wk.refreshing') : t('wk.refresh')}
          </button>
        </div>
      </header>

      {error ? (
        <div className="workers-monitor-error" role="alert">
          <strong>{t('wk.unavailable')}</strong>
          <span>{error}</span>
        </div>
      ) : (
        <div className="workers-monitor-body">
          <div className="workers-monitor-context">
            <span
              className={`workers-monitor-state ${unhealthy ? 'is-warn' : ''}`}
            >
              <i />{' '}
              {selectedEnvironment?.activeDeploymentId
                ? t('wk.deployed')
                : t('wk.notDeployed')}
            </span>
            {telemetry.latestAt && (
              <span>
                {t('wk.h.lastTelemetry', { age: age(telemetry.latestAt) })}
              </span>
            )}
          </div>

          <KpiGrid>
            <KpiCard
              size="lg"
              label={t('wk.h.availability')}
              value={percent(telemetry.availability)}
              hint={t('wk.h.availabilityNote')}
              accent={telemetry.serverFailures ? '#e5534b' : undefined}
            />
            <KpiCard
              size="lg"
              label={t('wk.h.success')}
              value={percent(telemetry.requestSuccessRate)}
              hint={t('wk.h.successNote')}
            />
            <KpiCard
              size="lg"
              label={t('wk.h.p95')}
              value={duration(telemetry.p95)}
              hint={t('wk.h.p50Note', { value: duration(telemetry.p50) })}
            />
            <KpiCard
              size="lg"
              label={t('wk.h.requests')}
              value={telemetry.total ? telemetry.total.toLocaleString() : '0'}
              hint={
                data.sampled
                  ? t('wk.h.sampledNote', { count: telemetry.captured })
                  : t('wk.h.requestsNote')
              }
            />
            <KpiCard
              size="lg"
              label={t('wk.h.errors')}
              value={String(telemetry.serverFailures)}
              hint={t('wk.h.errorsNote', { count: telemetry.exceptions })}
              accent={unhealthy ? '#e5534b' : undefined}
            />
            <KpiCard
              size="lg"
              label={t('wk.h.spendToday')}
              value={exactMoney(spend?.customerAccruedUsd)}
              hint={
                spend?.budgetRemainingUsd != null
                  ? `${t('wk.h.budgetLeft')} ${exactMoney(spend.budgetRemainingUsd)}`
                  : t('wk.h.budgetNote', {
                      limit: exactMoney(selectedEnvironment?.dailyBudgetUsd),
                    })
              }
            />
            <KpiCard
              size="lg"
              label={t('wk.h.release')}
              value={
                deployment
                  ? age(deployment.updatedAt || deployment.createdAt)
                  : '—'
              }
              hint={
                deployment
                  ? t('wk.h.releaseNote', {
                      age: age(deployment.updatedAt || deployment.createdAt),
                    })
                  : t('wk.h.noRelease')
              }
            />
          </KpiGrid>

          <div className="workers-monitor-grid">
            <section className="workers-monitor-subpanel">
              <div className="row between">
                <strong>{t('wk.h.traffic')}</strong>
                <span className="micro-mono">
                  {telemetry.earliestAt && telemetry.latestAt
                    ? `${new Date(telemetry.earliestAt).toLocaleString()} – ${new Date(telemetry.latestAt).toLocaleString()}`
                    : '—'}
                </span>
              </div>
              {telemetry.total ? (
                <TrafficBars
                  buckets={telemetry.buckets}
                  labels={{
                    requests: t('wk.h.requests'),
                    errors: t('wk.h.errors'),
                  }}
                />
              ) : (
                <div className="workers-monitor-empty">
                  {t('wk.h.noTraffic')}
                </div>
              )}
            </section>

            <section className="workers-monitor-subpanel">
              <div className="row between">
                <strong>{t('wk.h.bindings')}</strong>
                <span className="micro">
                  {(selectedEnvironment?.resources || []).length}
                </span>
              </div>
              <BindingList
                t={t}
                resources={selectedEnvironment?.resources || []}
                onSelect={onOpenResource}
              />
            </section>
          </div>

          <section className="workers-monitor-subpanel">
            <div className="row between">
              <div>
                <strong>{t('wk.h.endpoints')}</strong>
              </div>
              <span className="micro">{t('wk.h.endpointsNote')}</span>
            </div>
            {telemetry.endpoints.length ? (
              <div
                className="workers-monitor-endpoints"
                role="table"
                aria-label="Endpoint health"
              >
                <div role="row" className="workers-monitor-endpoint-head">
                  <span>{t('wk.h.col.endpoint')}</span>
                  <span>{t('wk.h.col.requests')}</span>
                  <span>{t('wk.h.col.4xx')}</span>
                  <span>{t('wk.h.col.5xx')}</span>
                  <span>{t('wk.h.col.p95')}</span>
                </div>
                {telemetry.endpoints.map((endpoint) => (
                  <div role="row" key={endpoint.key}>
                    <span>
                      <MethodPill method={endpoint.method} />{' '}
                      <code>{endpoint.path}</code>
                    </span>
                    <span className="mono">{endpoint.calls}</span>
                    <span className="mono">{endpoint.clientErrors}</span>
                    <span
                      className={`mono ${endpoint.serverErrors ? 'is-error' : ''}`}
                    >
                      {endpoint.serverErrors}
                    </span>
                    <span className="mono">{duration(endpoint.p95)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="workers-monitor-empty">
                {t('wk.h.noEndpoints')}
              </div>
            )}
          </section>

        </div>
      )}
    </section>
  );
};

export default WorkersMonitoringPanel;
